import { type ReactNode, useState, useEffect, useRef } from "react";

interface DrawerOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  side: "left" | "right";
  children: ReactNode;
}

type Phase = "closed" | "open" | "exiting";

const DRAWER_ANIMATION_MS = 300;

export const DrawerOverlay = ({
  isOpen,
  onClose,
  side,
  children,
}: DrawerOverlayProps) => {
  const [phase, setPhase] = useState<Phase>("closed");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      setPhase("open");
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    } else if (phase === "open") {
      setPhase("exiting");
      timeoutRef.current = setTimeout(() => {
        setPhase("closed");
        timeoutRef.current = null;
      }, DRAWER_ANIMATION_MS);
    }
  }, [isOpen, phase]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleAnimationEnd = () => {
    setPhase((p) => {
      if (p === "exiting") {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        return "closed";
      }
      return p;
    });
  };

  if (phase === "closed") return null;

  const isLeft = side === "left";
  const isExiting = phase === "exiting";
  const panelClass = isLeft
    ? isExiting
      ? "animate-slide-out-left"
      : "animate-slide-in-left"
    : isExiting
      ? "animate-slide-out-right"
      : "animate-slide-in-right";

  return (
    <div className="fixed inset-x-0 bottom-0 top-[calc(var(--height-navbar-mobile)+var(--height-env-banner))] lg:top-[calc(var(--height-navbar)+var(--height-env-banner))] z-40 xl:hidden">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`absolute top-0 bottom-0 ${isLeft ? "left-0 sidebar-width border-r" : "right-0 sidebar-right-width border-l"} border-global-secondary shadow-global-card overflow-hidden flex flex-col bg-global-primary ${panelClass}`}
        onAnimationEnd={handleAnimationEnd}
      >
        {children}
      </div>
    </div>
  );
};
